import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { generateOrderPayout } from 'utils/OrderWebAPIUtils';
import { BadgeAlt as Badge, Button, Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, Switch, Checkbox, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, useToast, } from '@cargomatic/ui';
import { ChevronDown, Loader2 } from 'lucide-react';
import formatMoney from 'utils/formatters/Money';
import MissingEquipmentWarning from './MissingEquipmentWarning';
function transformSelectedAPCharges(selectedIds, charges, groupedChargeTypes) {
    const selectedIdSet = new Set(selectedIds);
    const byShipment = {};
    charges.forEach((charge) => {
        var _a, _b;
        if (selectedIdSet.has(charge._id)) {
            const key = ((_a = charge.shipment) === null || _a === void 0 ? void 0 : _a._id) || 'order-level';
            if (!byShipment[key]) {
                byShipment[key] = [];
            }
            byShipment[key].push(((_b = charge.shipment) === null || _b === void 0 ? void 0 : _b._id) ? charge.adjustment_id : charge._id);
        }
    });
    return Object.entries(byShipment).reduce((acc, [shipmentId, selected]) => {
        if (selected.length > 0) {
            const shipmentChargeTypes = new Set(charges
                .filter((c) => {
                var _a;
                return selected.includes(((_a = c.shipment) === null || _a === void 0 ? void 0 : _a._id) ? c.adjustment_id : c._id) &&
                    c.type;
            })
                .map((c) => c.type));
            const shipmentGroupedTypes = groupedChargeTypes.filter((t) => shipmentChargeTypes.has(t));
            acc[shipmentId] = {
                selected,
                groupedChargeTypes: shipmentGroupedTypes,
            };
        }
        return acc;
    }, {});
}
export function GeneratePayoutDialog({ open, onClose, onSuccess, orderId, charges, initialSelectedIds, }) {
    const { toast } = useToast();
    const [dialogSelected, setDialogSelected] = useState(new Set());
    const [groupAllLikeItems, setGroupAllLikeItems] = useState(false);
    const [individualGroupedTypes, setIndividualGroupedTypes] = useState(new Set());
    // Only show selected charges with non-zero cost in the payout dialog
    const nonZeroCharges = useMemo(() => charges.filter((c) => c.value !== 0 && initialSelectedIds.has(c._id)), [charges, initialSelectedIds]);
    // Reset state when dialog opens
    useEffect(() => {
        if (open) {
            // Only pre-select charges that have non-zero cost
            const nonZeroIds = new Set(nonZeroCharges.map((c) => c._id));
            const filteredInitial = new Set(Array.from(initialSelectedIds).filter((id) => nonZeroIds.has(id)));
            setDialogSelected(filteredInitial);
            setGroupAllLikeItems(false);
            setIndividualGroupedTypes(new Set());
        }
    }, [open, initialSelectedIds, nonZeroCharges]);
    // Payout generation mutation
    const payoutMutation = useMutation({
        mutationFn: (params) => generateOrderPayout(orderId, params.payoutDate, params.selectedCharges, params.autoApprove),
        onSuccess: () => {
            toast({
                title: 'Payout Generated',
                description: 'Payout has been generated successfully.',
            });
            onSuccess();
        },
        onError: (err) => {
            var _a, _b;
            const message = (_b = (_a = err === null || err === void 0 ? void 0 : err.entity) === null || _a === void 0 ? void 0 : _a.message) !== null && _b !== void 0 ? _b : 'An error occurred.';
            toast({
                title: 'Payout Generation Failed',
                description: message,
                variant: 'destructive',
            });
        },
    });
    // Computed values
    const chargeTypeCounts = useMemo(() => {
        const counts = new Map();
        nonZeroCharges.forEach((charge) => {
            var _a, _b;
            const type = (_a = charge.type) !== null && _a !== void 0 ? _a : '';
            counts.set(type, ((_b = counts.get(type)) !== null && _b !== void 0 ? _b : 0) + 1);
        });
        return counts;
    }, [nonZeroCharges]);
    const groupableTypes = useMemo(() => {
        const types = [];
        chargeTypeCounts.forEach((count, type) => {
            if (count > 1)
                types.push(type);
        });
        return types;
    }, [chargeTypeCounts]);
    const grandTotal = useMemo(() => {
        return nonZeroCharges
            .filter((c) => dialogSelected.has(c._id))
            .reduce((sum, c) => { var _a; return sum + ((_a = c.value) !== null && _a !== void 0 ? _a : 0); }, 0);
    }, [nonZeroCharges, dialogSelected]);
    const orderLevelCharges = useMemo(() => nonZeroCharges.filter((charge) => { var _a; return !((_a = charge.shipment) === null || _a === void 0 ? void 0 : _a.shipment_id); }), [nonZeroCharges]);
    const shipmentChargesByShipment = useMemo(() => {
        const grouped = {};
        nonZeroCharges.forEach((charge) => {
            var _a;
            if ((_a = charge.shipment) === null || _a === void 0 ? void 0 : _a.shipment_id) {
                if (!grouped[charge.shipment.shipment_id]) {
                    grouped[charge.shipment.shipment_id] = {
                        charges: [],
                        shipmentObjId: charge.shipment._id,
                    };
                }
                grouped[charge.shipment.shipment_id].charges.push(charge);
            }
        });
        return grouped;
    }, [nonZeroCharges]);
    // Handlers
    const handleToggleCharge = useCallback((chargeId) => {
        setDialogSelected((prev) => {
            const next = new Set(prev);
            if (next.has(chargeId)) {
                next.delete(chargeId);
            }
            else {
                next.add(chargeId);
            }
            return next;
        });
    }, []);
    const handleToggleShipmentAll = useCallback((shipmentId, checked) => {
        const shipmentData = shipmentChargesByShipment[shipmentId];
        if (!shipmentData)
            return;
        setDialogSelected((prev) => {
            const next = new Set(prev);
            shipmentData.charges.forEach((c) => {
                if (checked) {
                    next.add(c._id);
                }
                else {
                    next.delete(c._id);
                }
            });
            return next;
        });
    }, [shipmentChargesByShipment]);
    const handleToggleAll = useCallback((mode) => {
        if (mode === 'all') {
            setDialogSelected(new Set(nonZeroCharges.map((c) => c._id)));
        }
        else {
            setDialogSelected(new Set());
        }
    }, [nonZeroCharges]);
    const handleGroupAll = useCallback((checked) => {
        setGroupAllLikeItems(checked);
        if (checked) {
            setIndividualGroupedTypes(new Set(groupableTypes));
        }
        else {
            setIndividualGroupedTypes(new Set());
        }
    }, [groupableTypes]);
    const handleGroupType = useCallback((type, checked) => {
        setIndividualGroupedTypes((prev) => {
            const next = new Set(prev);
            if (checked) {
                next.add(type);
            }
            else {
                next.delete(type);
            }
            const allGrouped = groupableTypes.length > 0 && groupableTypes.every((t) => next.has(t));
            setGroupAllLikeItems(allGrouped);
            return next;
        });
    }, [groupableTypes]);
    const handleGenerate = useCallback(() => {
        if (!orderId)
            return;
        const selectedIds = Array.from(dialogSelected);
        const groupedTypes = Array.from(individualGroupedTypes);
        const payload = transformSelectedAPCharges(selectedIds, nonZeroCharges, groupedTypes);
        if (Object.keys(payload).length === 0)
            return;
        payoutMutation.mutate({
            payoutDate: new Date().toISOString(),
            selectedCharges: payload,
            autoApprove: true,
        });
    }, [
        orderId,
        dialogSelected,
        individualGroupedTypes,
        nonZeroCharges,
        payoutMutation,
    ]);
    // Helpers for per-shipment totals and select-all state
    const getShipmentTotal = (shipmentCharges) => {
        return shipmentCharges
            .filter((c) => dialogSelected.has(c._id))
            .reduce((sum, c) => { var _a; return sum + ((_a = c.value) !== null && _a !== void 0 ? _a : 0); }, 0);
    };
    const isAllShipmentSelected = (shipmentCharges) => {
        return (shipmentCharges.length > 0 &&
            shipmentCharges.every((c) => dialogSelected.has(c._id)));
    };
    return (React.createElement(Dialog, { open: open, onOpenChange: onClose },
        React.createElement(DialogContent, { className: "max-w-3xl max-h-[90vh] overflow-y-auto" },
            React.createElement(DialogHeader, null,
                React.createElement(DialogTitle, { className: "text-xl font-semibold" }, "Generate Payout")),
            React.createElement("div", { className: "space-y-4 mt-2" },
                React.createElement("div", { className: "flex justify-between" },
                    React.createElement("div", null,
                        React.createElement("p", { className: "mb-4" },
                            "Select the charges you would like to include in this",
                            ' ',
                            React.createElement("span", { className: "font-bold" }, "Payout"))),
                    React.createElement("div", { className: "flex flex-col items-end gap-2" },
                        React.createElement(DropdownMenu, null,
                            React.createElement(DropdownMenuTrigger, { asChild: true },
                                React.createElement(Button, { variant: "outline", size: "sm" },
                                    "Select",
                                    React.createElement(ChevronDown, { className: "ml-1 h-4 w-4" }))),
                            React.createElement(DropdownMenuContent, { align: "end" },
                                React.createElement(DropdownMenuItem, { onClick: () => handleToggleAll('all') }, "All"),
                                React.createElement(DropdownMenuItem, { onClick: () => handleToggleAll('none') }, "None"))),
                        React.createElement("div", { className: "flex items-center gap-2" },
                            React.createElement("label", { htmlFor: "group-all-like-items", className: "text-xs font-medium" }, "Group like items"),
                            React.createElement(Switch, { id: "group-all-like-items", checked: groupAllLikeItems, onCheckedChange: handleGroupAll })))),
                React.createElement("div", { className: "space-y-4" },
                    orderLevelCharges.length > 0 && (React.createElement("div", { className: "bg-white rounded shadow border" },
                        React.createElement("div", { className: "flex items-center justify-between p-4 border-b" },
                            React.createElement("div", { className: "flex items-center gap-3" },
                                React.createElement(Checkbox, { checked: orderLevelCharges.every((c) => dialogSelected.has(c._id)), onCheckedChange: (checked) => {
                                        setDialogSelected((prev) => {
                                            const next = new Set(prev);
                                            orderLevelCharges.forEach((c) => {
                                                if (checked) {
                                                    next.add(c._id);
                                                }
                                                else {
                                                    next.delete(c._id);
                                                }
                                            });
                                            return next;
                                        });
                                    } }),
                                React.createElement("span", { className: "text-xl font-semibold" }, "Order Level")),
                            React.createElement("span", { className: "text-xl text-muted-foreground" }, formatMoney(orderLevelCharges
                                .filter((c) => dialogSelected.has(c._id))
                                .reduce((sum, c) => { var _a; return sum + ((_a = c.value) !== null && _a !== void 0 ? _a : 0); }, 0)))),
                        React.createElement("div", { className: "p-4 space-y-4" }, orderLevelCharges.map((charge) => {
                            var _a, _b, _c, _d, _e, _f;
                            const isSelected = dialogSelected.has(charge._id);
                            const isGroupable = ((_b = chargeTypeCounts.get((_a = charge.type) !== null && _a !== void 0 ? _a : '')) !== null && _b !== void 0 ? _b : 0) > 1;
                            return (React.createElement("div", { key: charge._id, className: "flex items-center justify-between" },
                                React.createElement("div", { className: "flex items-center gap-3 flex-1" },
                                    React.createElement(Checkbox, { checked: isSelected, onCheckedChange: () => handleToggleCharge(charge._id) }),
                                    React.createElement("div", { className: "flex items-center gap-2 flex-1" },
                                        React.createElement("span", { className: "text-muted-foreground" }, charge.message),
                                        ((_c = charge.equipment) === null || _c === void 0 ? void 0 : _c.equipment_id) ? (React.createElement(Badge, { variant: "secondary" }, charge.equipment.equipment_id)) : (!((_e = (_d = charge.invoice_id) === null || _d === void 0 ? void 0 : _d.number) === null || _e === void 0 ? void 0 : _e.startsWith('CM-')) && (React.createElement(MissingEquipmentWarning, null)))),
                                    isGroupable && (React.createElement("div", { className: "flex items-center gap-2 font-semibold" },
                                        React.createElement("span", { className: "text-xs" }, "Group"),
                                        React.createElement(Switch, { checked: individualGroupedTypes.has((_f = charge.type) !== null && _f !== void 0 ? _f : ''), onCheckedChange: (checked) => { var _a; return handleGroupType((_a = charge.type) !== null && _a !== void 0 ? _a : '', checked); }, disabled: !isSelected })))),
                                React.createElement("span", { className: "ml-4 text-lg text-muted-foreground" }, formatMoney(charge.value))));
                        })))),
                    Object.entries(shipmentChargesByShipment).map(([shipmentId, { charges: shipmentCharges, shipmentObjId }]) => {
                        const shipmentTotal = getShipmentTotal(shipmentCharges);
                        const allSelected = isAllShipmentSelected(shipmentCharges);
                        const hasCharges = shipmentCharges.length > 0;
                        return (React.createElement("div", { key: shipmentId, className: "bg-white rounded shadow border" },
                            React.createElement("div", { className: "flex items-center justify-between p-4 border-b" },
                                React.createElement("div", { className: "flex items-center gap-3" },
                                    React.createElement(Checkbox, { checked: allSelected, onCheckedChange: (checked) => handleToggleShipmentAll(shipmentId, checked) }),
                                    React.createElement("a", { href: `/shipments/${shipmentObjId}`, target: "_blank", rel: "noopener noreferrer", className: "text-xl font-semibold text-black hover:underline" }, shipmentId)),
                                React.createElement("span", { className: "text-xl text-muted-foreground" }, formatMoney(shipmentTotal))),
                            React.createElement("div", { className: "p-4" }, hasCharges ? (React.createElement("div", { className: "space-y-4" }, shipmentCharges.map((charge) => {
                                var _a, _b, _c, _d;
                                const isSelected = dialogSelected.has(charge._id);
                                const isGroupable = ((_b = chargeTypeCounts.get((_a = charge.type) !== null && _a !== void 0 ? _a : '')) !== null && _b !== void 0 ? _b : 0) >
                                    1;
                                return (React.createElement("div", { key: charge._id, className: "flex items-center justify-between" },
                                    React.createElement("div", { className: "flex items-center gap-3 flex-1" },
                                        React.createElement(Checkbox, { checked: isSelected, onCheckedChange: () => handleToggleCharge(charge._id) }),
                                        React.createElement("div", { className: "flex items-center gap-2 flex-1" },
                                            React.createElement("span", { className: "text-muted-foreground" }, charge.message),
                                            ((_c = charge.equipment) === null || _c === void 0 ? void 0 : _c.equipment_id) && (React.createElement(Badge, { variant: "secondary" }, charge.equipment.equipment_id))),
                                        isGroupable && (React.createElement("div", { className: "flex items-center gap-2 font-semibold" },
                                            React.createElement("span", { className: "text-xs" }, "Group"),
                                            React.createElement(Switch, { checked: individualGroupedTypes.has((_d = charge.type) !== null && _d !== void 0 ? _d : ''), onCheckedChange: (checked) => {
                                                    var _a;
                                                    return handleGroupType((_a = charge.type) !== null && _a !== void 0 ? _a : '', checked);
                                                }, disabled: !isSelected })))),
                                    React.createElement("span", { className: "ml-4 text-lg text-muted-foreground" }, formatMoney(charge.value))));
                            }))) : (React.createElement("p", { className: "text-muted-foreground" }, "There are no charges")))));
                    }))),
            React.createElement(DialogFooter, { className: "flex items-center justify-between sm:justify-between mt-4" },
                React.createElement("span", { className: "text-2xl font-semibold text-muted-foreground" }, formatMoney(grandTotal)),
                React.createElement("div", { className: "flex items-center gap-3" },
                    React.createElement(Button, { className: "bg-zinc-900 text-white hover:bg-zinc-800", onClick: handleGenerate, disabled: payoutMutation.isLoading || dialogSelected.size === 0 },
                        payoutMutation.isLoading && (React.createElement(Loader2, { className: "mr-2 h-4 w-4 animate-spin" })),
                        payoutMutation.isLoading ? 'Generating...' : 'Generate Payout'),
                    React.createElement(Button, { variant: "ghost", onClick: onClose, disabled: payoutMutation.isLoading }, "Cancel"))))));
}
export default GeneratePayoutDialog;
